import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import Color from 'color';

import Theme from 'LegacyWeployDesignLanguage/Theme';

const Container = styled.div`
  position: relative;
`;

const MatchingCandidates = styled.div`
  position: relative;
  font-size: ${props => props.size}; /* This controls the size. */
  display: inline-block;
`;

const File = styled.div`
  position: relative;
  height: 1em;
  width: 0.8em;
  border-radius: 0.1em;
  border-left: ${props => `0.03em solid ${props.fileColor}`};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    position: absolute;
    content: "";
    left: 0;
    right: -6px;
    height: 23%;
    top: 0px;
    border: ${props => `0.02em solid ${props.fileColor}`};
    border-width: 0.03em 0.04em 0 0;
    transform: skew(45deg);
    transform-origin: right bottom;
  }

  &::after {
    position: absolute;
    content: "";
    left: 0;
    right: 0;
    bottom: 0;
    height: calc(80% - 0.04em);
    border: ${props => `0.03em solid ${props.fileColor}`};
    border-width: 0 0.03em 0.03em 0;
    border-bottom-right-radius: 0.1em;
  }
`;

const FileEdge = styled.div`
  content: "";
  color: ${props => props.color};
  display: block;
  position: absolute;
  top: 0;
  right: -2px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0.12em 0.12em;
  border-color: ${props => `0.02em solid ${props.color}`};
  box-shadow: -0.02em 0.02em 0.02em rgba(0, 0, 0, 0.1);
  border-top-color: transparent;
  border-right-color: transparent;
  border-bottom-left-radius: 0.05em;
`;

const magnifyingGlassKeyframes = keyframes`
  0% {
    transform: translate(0%, 0%);
  }
  25% {
    transform: translate(10%, 50%);
  }
  50% {
    transform: translate(50%, 70%) rotate(90deg);
  }
  75% {
    transform: translate(70%, 0%) rotate(75deg);
  }
`;

const MagnifyingGlass = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  width: 0.4em;
  height: 0.4em;
  border: ${props => `0.1em solid ${props.color}`};
  border-radius: 50%;
  box-shadow: 0.01em 0.01em 0.01em 0px rgba(0, 0, 0, 0.5);
  animation: ${props => css`${magnifyingGlassKeyframes} ${props.speed} ${props.ease} infinite`};
  animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 2);

  &::before {
    content: "";
    display: inline-block;
    position: absolute;
    right: -0.26em;
    bottom: -0.11em;
    border-width: 0;
    background: ${props => props.color};
    width: 0.35em;
    height: 0.1em;
    border-radius: 8em;
    z-index: -1;
    -webkit-transform: rotate(45deg);
    -moz-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    -o-transform: rotate(45deg);
  }

  &::after {
    content: "";
    display: inline-block;
    position: absolute;
    right: -0.26em;
    bottom: -0.11em;
    border-width: 0;
    background: ${props => props.color};
    width: 0.35em;
    height: 0.1em;
    border-radius: 8em;
    -webkit-transform: rotate(45deg);
  }
`;

const fileLinesKeyframes = keyframes`
  0% {
    transform-origin: 0 100%;
    transform: scaleX(0);
  }
  50% {
    transform-origin: 0 100%;
    transform: scaleX(1);
  }
  75% {
    transform-origin: 100% 0%;
    transform: scaleX(1);
  }
  100% {
    transform-origin: 100% 0%;
    transform: scaleX(0);
  }
`;

const Line = styled.div`
  width: 0.6em;
  height: 0.05em;
  margin-bottom: 0.065em;
  background: ${props => Color(props.theme.colors.weployAqua).lighten(0.4).string()};

  &.one {
    animation: ${props => css`${fileLinesKeyframes} ${props.speed} ease-out infinite`};
  }
  &.two {
    animation: ${props => css`${fileLinesKeyframes} ${props.speed} ease-out infinite`};
    animation-delay: 0.2s;
  }
  &.three {
    animation: ${props => css`${fileLinesKeyframes} ${props.speed} ease-out infinite`};
    animation-delay: 0.4s;
  }
  &.four {
    animation: ${props => css`${fileLinesKeyframes} ${props.speed} ease-out infinite`};
    animation-delay: 0.6s;
  }
`;

const SearchingIcon = ({
  ease,
  fileColor,
  magnifyingGlassColor,
  size,
  speed,
}) => (
  <Container>
    <MatchingCandidates size={size}>
      <File fileColor={fileColor}>
        <FileEdge color={fileColor} />
        <div>
          <Line color={fileColor} className="one" speed={speed} />
          <Line color={fileColor} className="two" speed={speed} />
          <Line color={fileColor} className="three" speed={speed} />
          <Line color={fileColor} className="four" speed={speed} />
        </div>
      </File>
      <MagnifyingGlass color={magnifyingGlassColor} speed={speed} ease={ease} />
    </MatchingCandidates>
  </Container>
);

SearchingIcon.propTypes = {
  ease: PropTypes.string,
  fileColor: PropTypes.string,
  magnifyingGlassColor: PropTypes.string,
  size: PropTypes.string,
  speed: PropTypes.string,
};

SearchingIcon.defaultProps = {
  fileColor: '#C2C2C2', /* Little bit out of normal theme colours so hard-coded */
  ease: 'ease-out',
  magnifyingGlassColor: Theme.colors.weployAqua,
  size: '6em',
  speed: '3s',
};

export default SearchingIcon;
